import React from "react";
import { useObserver } from "mobx-react-lite";
import { useHistory, useParams } from "react-router";
import { sumBy } from "lodash";
import { Grid, IconButton, Select, MenuItem } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ArrowBack as ArrowBackIcon } from "@material-ui/icons";
import { Formatter } from "libs";
import { Destination } from "api";
import { Link, Table } from "components";
import { StoreProvider, useStore } from "./Store";

const useStyles = makeStyles({
  table: {
    "& table": {
      whiteSpace: "nowrap",
    },
  },
});

interface Params {
  developerId: string;
  appId: string;
}

export const Page = () => {
  const store = useStore();
  const history = useHistory();
  const params = useParams<Params>();
  const styles = useStyles();

  React.useEffect(() => {
    store.fetch(+params.developerId, +params.appId);
  }, [params.appId, params.developerId, store]);

  const handleDays = React.useCallback(
    (e: React.ChangeEvent<{ name?: string; value: any }>) => {
      store.days.selected = e.target.value;
      store.fetch(+params.developerId, +params.appId);
    },
    [store, params.developerId, params.appId]
  );

  const data = React.useMemo(
    () =>
      [
        { id: Destination.RED, label: "Red" },
        { id: Destination.TWITTER, label: "Twitter" },
        { id: Destination.ORGANIC, label: "オーガニック" },
      ].map((destination) => {
        const adReports = store.adReports.filter(
          (r) => r.destination === destination.id
        );
        const clicks = sumBy(adReports, (r) => r.clicks);
        const impressions = sumBy(adReports, (r) => r.impressions);
        const price = sumBy(adReports, (r) => r.price);
        const directConversions = sumBy(adReports, (r) => r.directConversions);

        const revenueReport = store.revenueReports.find(
          (report) =>
            report.destination === destination.id &&
            report.days === store.days.options[store.days.selected].days
        );

        return {
          ...destination,
          arpu:
            revenueReport && revenueReport.uniqueLaunchedCount > 0
              ? revenueReport.price / revenueReport.uniqueLaunchedCount
              : 0,
          arppu:
            revenueReport && revenueReport.uniquePurchasedCount > 0
              ? revenueReport.price / revenueReport.uniquePurchasedCount
              : 0,
          revenue: revenueReport ? revenueReport.price : 0,
          roas: revenueReport && price > 0 ? revenueReport.price / price : 0,
          clicks,
          impressions,
          price,
          directConversions,
          ctr: impressions ? (clicks / impressions) * 100 : 0,
          cpc: clicks ? price / clicks : 0,
          cpm: impressions ? (price / impressions) * 1000 : 0,
          cpa: directConversions ? price / directConversions : 0,
          cvr: clicks ? (directConversions / clicks) * 100 : 0,
        };
      }),
    [store.revenueReports, store.adReports, store.days]
  );

  return useObserver(() => (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs="auto">
        <IconButton color="primary" onClick={history.goBack}>
          <ArrowBackIcon />
        </IconButton>
      </Grid>

      <Grid item xs={12}>
        <Table
          className={styles.table}
          title={
            <Select value={store.days.selected} onChange={handleDays}>
              {store.days.options.map((option, i) => (
                <MenuItem key={option.label} value={i}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          }
          isLoading={store.loading}
          options={{
            exportFileName: "レポート",
          }}
          data={data}
          columns={[
            {
              title: "名前",
              field: "label",
              render: (report) => (
                <Link
                  to={`/developers/${params.developerId}/apps/${params.appId}/destinations/${report.id}`}
                >
                  {report.label}
                </Link>
              ),
            },
            {
              title: "ARPU",
              field: "arpu",
              type: "currency",
              currencySetting: {
                locale: "ja",
                currencyCode: "JPY",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              },
            },
            {
              title: "ARPPU",
              field: "arppu",
              type: "currency",
              currencySetting: {
                locale: "ja",
                currencyCode: "JPY",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              },
            },
            {
              title: "売上",
              field: "revenue",
              type: "currency",
              currencySetting: {
                locale: "ja",
                currencyCode: "JPY",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              },
            },
            {
              title: "ROAS",
              field: "roas",
              type: "numeric",
              render: (report) => Formatter.percentage(report.roas),
            },
            {
              title: "クリック",
              field: "clicks",
              type: "numeric",
            },
            {
              title: "表示回数",
              field: "impressions",
              type: "numeric",
            },
            {
              title: "広告費",
              field: "price",
              type: "numeric",
            },
            {
              title: "CV数",
              field: "directConversions",
              type: "numeric",
            },
            {
              title: "CTR",
              field: "ctr",
              type: "numeric",
              render: (report) => Formatter.percentage(report.ctr),
            },
            {
              title: "CPC",
              field: "cpc",
              type: "currency",
              currencySetting: {
                locale: "ja",
                currencyCode: "JPY",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              },
            },
            {
              title: "CPM",
              field: "cpm",
              type: "currency",
              currencySetting: {
                locale: "ja",
                currencyCode: "JPY",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              },
            },
            {
              title: "CPA",
              field: "cpa",
              type: "currency",
              currencySetting: {
                locale: "ja",
                currencyCode: "JPY",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              },
            },
            {
              title: "CVR",
              field: "cvr",
              type: "numeric",
              render: (report) => Formatter.percentage(report.cvr),
            },
          ]}
        />
      </Grid>
    </Grid>
  ));
};

export default () => (
  <StoreProvider>
    <Page />
  </StoreProvider>
);
